/**
 * @file services/dayTags.js
 * @description Firestore CRUD for per-day tags/badges (e.g., "free day", "deficit day").
 */

import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Get the tag for a specific date.
 * @param {string} uid
 * @param {string} dateKey - format "YYYY-MM-DD" or display date string
 * @returns {Promise<string|null>}
 */
export async function getDayTag(uid, dateKey) {
  const ref = doc(db, "users", uid, "dayTags", dateKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data().tag || null;
}

/**
 * Get all day tags for a user (batch load).
 * @param {string} uid
 * @returns {Promise<Record<string, string>>} Map of dateKey → tag
 */
export async function getAllDayTags(uid) {
  const { getDocs, collection } = await import("firebase/firestore");
  const colRef = collection(db, "users", uid, "dayTags");
  const snap = await getDocs(colRef);
  const tags = {};
  snap.forEach((doc) => {
    tags[doc.id] = doc.data().tag;
  });
  return tags;
}

/**
 * Set a tag for a specific date.
 * @param {string} uid
 * @param {string} dateKey
 * @param {string} tag
 */
export async function setDayTag(uid, dateKey, tag) {
  const ref = doc(db, "users", uid, "dayTags", dateKey);
  await setDoc(ref, { tag, updatedAt: new Date().toISOString() }, { merge: true });
}

/**
 * Remove a tag for a specific date.
 * @param {string} uid
 * @param {string} dateKey
 */
export async function removeDayTag(uid, dateKey) {
  const { deleteDoc } = await import("firebase/firestore");
  const ref = doc(db, "users", uid, "dayTags", dateKey);
  await deleteDoc(ref);
}
